import { useEffect, useRef, useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button } from "@fawkes/ui";

import { getAccessToken } from "../auth";
import { restoreSession } from "../authProvider";

// ── Nazorg → Lay-out en thema ──────────────────────────────────────────────────────────────────────
// Het asiel bepaalt hier hoe de nazorgvragenlijst bij de adoptant op het scherm komt: kleuren, logo,
// letter en de teksten eromheen. Eén huisstijl voor de hele organisatie (besluit 2026-09-23), dus geen
// keuze per vestiging of per vragenlijst.
//
// De hulp zit in "haal uit mijn website": de api leest de site, leidt er een palet uit af en pakt het
// eerste beeld dat op een logo lijkt. Wat eruit komt is een voorstel — alles blijft met de hand bij te
// stellen, en rechts zie je meteen wat het wordt.

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

export interface Huisstijl {
  primair: string;
  accent: string;
  achtergrond: string;
  oppervlak: string;
  tekst: string;
  lettertype: string;
  kopTitel: string | null;
  welkomTekst: string | null;
  slotTekst: string | null;
  website: string | null;
  logo: boolean;
}

interface Voorstel {
  bron: string;
  primair: string;
  accent: string;
  achtergrond: string;
  oppervlak: string;
  tekst: string;
  gevonden: string[];
  logoGevonden: boolean;
}

const LETTERTYPES = ["SYSTEEM", "SCHREEF", "ROND", "SMAL"] as const;

/** Dezelfde stapels als op het platform; hier alleen om het voorbeeld kloppend te tonen. */
export function letterStapel(lettertype: string): string {
  switch (lettertype) {
    case "SCHREEF":
      return 'Georgia, "Times New Roman", serif';
    case "ROND":
      return '"Trebuchet MS", "Segoe UI", system-ui, sans-serif';
    case "SMAL":
      return '"Arial Narrow", "Segoe UI", system-ui, sans-serif';
    default:
      return 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
  }
}

/** Een kleur plus doorzichtigheid, voor zachte vlakken achter de accentkleur. */
function zacht(hex: string, deel: number): string {
  return `color-mix(in srgb, ${hex} ${Math.round(deel * 100)}%, transparent)`;
}

export function NazorgHuisstijlTab({ k }: { k: (suffix: string) => string }) {
  const t = useTranslate();
  const { data, isLoading, refetch } = useCustom<Huisstijl>({ url: "huisstijl", method: "get" });
  const { mutate } = useCustomMutation();

  const [vorm, setVorm] = useState<Huisstijl | null>(null);
  const [website, setWebsite] = useState("");
  const [bezig, setBezig] = useState<"" | "website" | "logo" | "opslaan">("");
  const [melding, setMelding] = useState<{ tekst: string; fout: boolean } | null>(null);
  const [gevonden, setGevonden] = useState<string[]>([]);
  // Verandert het logo, dan moet de browser het opnieuw ophalen: hetzelfde adres met een andere sleutel.
  const [logoStand, setLogoStand] = useState(0);
  const bestandRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (data?.data) {
      setVorm(data.data);
      setWebsite(data.data.website ?? "");
    }
  }, [data]);

  /**
   * Het bewaarde logo, opgehaald als blob.
   *
   * <p>Niet als {@code <img src="/api/huisstijl/logo">}: dat endpoint zit achter de login en een
   * {@code <img>} kan geen bearer meesturen, dus die bleef leeg. Dan zie je na het uploaden nergens
   * je logo terug en lijkt het alsof er niets bewaard is — terwijl het er wel staat.
   *
   * <p>Bij een 401 eerst de sessie herstellen: na een herstart van de api is het token in het geheugen
   * verlopen, en de rest van het scherm merkt daar niets van omdat dat via de dataprovider loopt.
   */
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!vorm?.logo) {
      setLogoUrl(null);
      return;
    }
    let actief = true;
    let url: string | null = null;
    const haal = async () => {
      const kop = (bearer: string | null | undefined) =>
        bearer ? { Authorization: `Bearer ${bearer}` } : undefined;
      let res = await fetch(`${API_URL}/huisstijl/logo`, { headers: kop(getAccessToken()) });
      if (res.status === 401 && (await restoreSession())) {
        res = await fetch(`${API_URL}/huisstijl/logo`, { headers: kop(getAccessToken()) });
      }
      if (!actief) return;
      url = res.ok ? URL.createObjectURL(await res.blob()) : null;
      setLogoUrl(url);
    };
    void haal().catch(() => { if (actief) setLogoUrl(null); });
    return () => {
      actief = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [vorm?.logo, logoStand]);

  if (isLoading || !vorm) {
    return <p className="muted">{t("common.loading")}</p>;
  }

  const zet = (veld: keyof Huisstijl, waarde: string) => setVorm((v) => (v ? { ...v, [veld]: waarde } : v));

  const opslaan = () => {
    setBezig("opslaan");
    setMelding(null);
    mutate(
      {
        url: "huisstijl",
        method: "put",
        values: { ...vorm, website },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setBezig("");
          setMelding({ tekst: k("huisstijl.opgeslagen"), fout: false });
          void refetch();
        },
        onError: () => {
          setBezig("");
          setMelding({ tekst: k("huisstijl.opslaanFout"), fout: true });
        },
      },
    );
  };

  const uitWebsite = () => {
    if (!website.trim()) return;
    setBezig("website");
    setMelding(null);
    mutate(
      {
        url: "huisstijl/uit-website",
        method: "post",
        values: { url: website.trim() },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          const voorstel = res?.data as Voorstel | undefined;
          setBezig("");
          setGevonden(voorstel?.gevonden ?? []);
          setLogoStand((n) => n + 1);
          setMelding({
            tekst: voorstel?.logoGevonden ? k("huisstijl.uitWebsiteKlaarMetLogo") : k("huisstijl.uitWebsiteKlaar"),
            fout: false,
          });
          void refetch();
        },
        onError: () => {
          setBezig("");
          setMelding({ tekst: k("huisstijl.uitWebsiteFout"), fout: true });
        },
      },
    );
  };

  /** Logo uploaden gaat buiten de dataprovider om: die stuurt json, dit is een bestand. */
  const stuurLogo = async (bestand: File, kleurenErbij: boolean) => {
    setBezig("logo");
    setMelding(null);
    const body = new FormData();
    body.append("file", bestand);
    const token = getAccessToken();
    try {
      const res = await fetch(`${API_URL}/huisstijl/${kleurenErbij ? "uit-logo" : "logo"}`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body,
      });
      if (!res.ok) throw new Error(String(res.status));
      const antwoord = (await res.json()) as Voorstel | Huisstijl;
      if (kleurenErbij && "gevonden" in antwoord) setGevonden(antwoord.gevonden);
      setLogoStand((n) => n + 1);
      setMelding({ tekst: k("huisstijl.logoKlaar"), fout: false });
      void refetch();
    } catch {
      setMelding({ tekst: k("huisstijl.logoFout"), fout: true });
    } finally {
      setBezig("");
      if (bestandRef.current) bestandRef.current.value = "";
    }
  };

  const wisLogo = () => {
    setBezig("logo");
    mutate(
      { url: "huisstijl/logo", method: "delete", values: {}, successNotification: false, errorNotification: false },
      {
        onSuccess: () => {
          setBezig("");
          setLogoStand((n) => n + 1);
          void refetch();
        },
        onError: () => {
          setBezig("");
          setMelding({ tekst: k("huisstijl.logoFout"), fout: true });
        },
      },
    );
  };

  return (
    <>
      <div className="nz__head-row">
        <div>
          <h1 className="nz__title">{k("huisstijl.title")}</h1>
          <p className="nz__subtitle">{k("huisstijl.subtitle")}</p>
        </div>
        <Button variant="primary" disabled={bezig !== ""} onClick={opslaan}>
          {k("huisstijl.opslaan")}
        </Button>
      </div>

      {melding && <p className={melding.fout ? "hs__melding is-fout" : "hs__melding"}>{melding.tekst}</p>}

      <div className="hs">
        <div className="card hs__velden">
          {/* De snelste weg: geef je website en laat de rest voorstellen. */}
          <section className="hs__blok">
            <h2 className="hs__kop">{k("huisstijl.aiKop")}</h2>
            <p className="muted">{k("huisstijl.aiUitleg")}</p>
            <div className="hs__rij">
              <input
                className="fui-field__input"
                placeholder="https://www.mijnasiel.nl"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
              <Button variant="secondary" disabled={bezig !== "" || !website.trim()} onClick={uitWebsite}>
                {bezig === "website" ? k("huisstijl.bezig") : k("huisstijl.aiKnop")}
              </Button>
            </div>
            {gevonden.length > 0 && (
              <div className="hs__gevonden" aria-label={k("huisstijl.gevonden")}>
                {gevonden.map((kleur) => (
                  <button
                    key={kleur}
                    type="button"
                    className="hs__staal"
                    style={{ background: kleur }}
                    title={`${k("huisstijl.gebruikAlsHoofdkleur")}: ${kleur}`}
                    onClick={() => zet("primair", kleur)}
                  />
                ))}
              </div>
            )}
          </section>

          <section className="hs__blok">
            <h2 className="hs__kop">{k("huisstijl.logoKop")}</h2>
            <div className="hs__rij">
              <input
                ref={bestandRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                aria-label={k("huisstijl.logoKies")}
                onChange={(e) => {
                  const bestand = e.target.files?.[0];
                  if (bestand) void stuurLogo(bestand, true);
                }}
              />
              {vorm.logo && (
                <Button variant="secondary" disabled={bezig !== ""} onClick={wisLogo}>
                  {k("huisstijl.logoWeg")}
                </Button>
              )}
            </div>
            {/* Wat er nú bewaard staat, naast de knop waar je het instelt. Het veld eronder zegt na een
                upload "geen bestand gekozen" — dat is de browser die zijn eigen veld leegmaakt en zegt
                niets over wat er bewaard is, maar zonder dit beeld is dat niet te zien. */}
            {vorm.logo && (
              <div className="hs__logo-nu">
                {logoUrl
                  ? <img src={logoUrl} alt={k("huisstijl.logoKop")} />
                  : <span className="muted">{k("huisstijl.bezig")}</span>}
              </div>
            )}
            <p className="muted">{k("huisstijl.logoUitleg")}</p>
          </section>

          <section className="hs__blok">
            <h2 className="hs__kop">{k("huisstijl.kleurenKop")}</h2>
            <div className="hs__kleuren">
              {(
                [
                  ["primair", k("huisstijl.kleur.primair")],
                  ["accent", k("huisstijl.kleur.accent")],
                  ["achtergrond", k("huisstijl.kleur.achtergrond")],
                  ["oppervlak", k("huisstijl.kleur.oppervlak")],
                  ["tekst", k("huisstijl.kleur.tekst")],
                ] as Array<[keyof Huisstijl, string]>
              ).map(([veld, label]) => (
                <label key={veld} className="hs__kleur">
                  <span>{label}</span>
                  <span className="hs__kleur-invoer">
                    <input
                      type="color"
                      value={String(vorm[veld] ?? "#000000")}
                      aria-label={label}
                      onChange={(e) => zet(veld, e.target.value)}
                    />
                    <input
                      className="fui-field__input hs__hex"
                      value={String(vorm[veld] ?? "")}
                      onChange={(e) => zet(veld, e.target.value)}
                    />
                  </span>
                </label>
              ))}
            </div>
          </section>

          <section className="hs__blok">
            <h2 className="hs__kop">{k("huisstijl.letterKop")}</h2>
            <div className="hs__letters">
              {LETTERTYPES.map((l) => (
                <button
                  key={l}
                  type="button"
                  className={"hs__letter" + (vorm.lettertype === l ? " is-aan" : "")}
                  style={{ fontFamily: letterStapel(l) }}
                  aria-pressed={vorm.lettertype === l}
                  onClick={() => zet("lettertype", l)}
                >
                  {k(`huisstijl.letter.${l}`)}
                </button>
              ))}
            </div>
          </section>

          <section className="hs__blok">
            <h2 className="hs__kop">{k("huisstijl.tekstenKop")}</h2>
            <label className="hs__veld">
              <span>{k("huisstijl.kopTitel")}</span>
              <input
                className="fui-field__input"
                placeholder={k("huisstijl.kopTitelHint")}
                value={vorm.kopTitel ?? ""}
                onChange={(e) => zet("kopTitel", e.target.value)}
              />
            </label>
            <label className="hs__veld">
              <span>{k("huisstijl.welkomTekst")}</span>
              <textarea
                className="fui-field__input"
                rows={3}
                placeholder={k("huisstijl.welkomTekstHint")}
                value={vorm.welkomTekst ?? ""}
                onChange={(e) => zet("welkomTekst", e.target.value)}
              />
            </label>
            <label className="hs__veld">
              <span>{k("huisstijl.slotTekst")}</span>
              <textarea
                className="fui-field__input"
                rows={2}
                placeholder={k("huisstijl.slotTekstHint")}
                value={vorm.slotTekst ?? ""}
                onChange={(e) => zet("slotTekst", e.target.value)}
              />
            </label>
          </section>
        </div>

        {/* Rechts: hetzelfde beeld dat de adoptant op zijn telefoon krijgt. */}
        <div className="card hs__voorbeeld">
          <h2 className="hs__kop">{k("huisstijl.voorbeeldKop")}</h2>
          <div
            className="phone"
            aria-label={k("huisstijl.voorbeeldAria")}
            style={{
              fontFamily: letterStapel(vorm.lettertype),
              ["--hs-bg" as string]: vorm.achtergrond,
              ["--hs-surface" as string]: vorm.oppervlak,
              ["--hs-ink" as string]: vorm.tekst,
              ["--hs-primair" as string]: vorm.primair,
              ["--hs-accent" as string]: vorm.accent,
              ["--hs-accent-zacht" as string]: zacht(vorm.accent, 0.12),
            }}
          >
            <div className="phone__notch" />
            <div className="phone__screen hs__screen">
              <div className="hs__hero">
                {vorm.logo && logoUrl ? (
                  <img className="hs__logo" src={logoUrl} alt="" />
                ) : (
                  <div className="hs__avatar">🐾</div>
                )}
                <div className="hs__kicker">{k("huisstijl.voorbeeldKicker")}</div>
                <div className="hs__titel">{vorm.kopTitel || k("huisstijl.voorbeeldTitel")}</div>
                <div className="hs__welkom">{vorm.welkomTekst || k("huisstijl.voorbeeldWelkom")}</div>
              </div>
              <div className="hs__inhoud">
                <div className="hs__vraag">
                  <div className="hs__vraag-label">{k("huisstijl.voorbeeldVraag1")}</div>
                  <div className="hs__opties">
                    <span className="hs__optie">😟</span>
                    <span className="hs__optie">😐</span>
                    <span className="hs__optie is-aan">🙂</span>
                    <span className="hs__optie">😀</span>
                  </div>
                </div>
                <div className="hs__vraag">
                  <div className="hs__vraag-label">{k("huisstijl.voorbeeldVraag2")}</div>
                  <div className="hs__opties">
                    <span className="hs__optie">{k("huisstijl.voorbeeldNee")}</span>
                    <span className="hs__optie is-aan">{k("huisstijl.voorbeeldBeetje")}</span>
                    <span className="hs__optie">{k("huisstijl.voorbeeldPrima")}</span>
                  </div>
                </div>
                <div className="hs__knop">{k("huisstijl.voorbeeldKnop")}</div>
                {vorm.slotTekst && <p className="hs__slot">{vorm.slotTekst}</p>}
              </div>
            </div>
          </div>
          <p className="muted hs__voet">{k("huisstijl.voorbeeldVoet")}</p>
        </div>
      </div>
    </>
  );
}
